/**
 * Reporting-period presets for the General Sales Report (PRD §5).
 *
 * Weeks run Sunday–Saturday to match the week boundaries svc-analytics already
 * uses (`models/analytics/revenueDateCondition.js`), so a preset here and a
 * week elsewhere in analytics mean the same thing.
 *
 * Ranges are built in the REPORT's timezone — the selected location's business
 * zone — not the browser's. svc-analytics groups a single-location report by
 * that location's zone (`generalSalesReportController.resolveTimezone`), so a
 * preset resolved against the browser clock asked for the wrong days: an admin
 * in IST picking "Today" for a Florida shop got the Florida date that had not
 * started yet, saw nothing, and found the orders under "Yesterday".
 *
 * Ranges never run past today: the report has no data for the future, and a
 * range ending tomorrow would skew "average cars per day" by adding empty days
 * to the divisor.
 */

export type DatePresetId =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'last-year'
  | 'custom';

export interface DatePresetOption {
  id: DatePresetId;
  label: string;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export const DATE_PRESET_OPTIONS: DatePresetOption[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this-week', label: 'This Week' },
  { id: 'last-week', label: 'Last Week' },
  { id: 'this-month', label: 'This Month' },
  { id: 'last-month', label: 'Last Month' },
  { id: 'this-quarter', label: 'This Quarter' },
  { id: 'last-quarter', label: 'Last Quarter' },
  { id: 'this-year', label: 'This Year' },
  { id: 'last-year', label: 'Last Year' },
  { id: 'custom', label: 'Custom Range' },
];

/** PRD §5: the report opens on the current month. */
export const DEFAULT_DATE_PRESET: DatePresetId = 'this-month';

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The calendar date it is *in `timeZone`* right now, as a local midnight Date.
 *
 * Deliberately a local Date carrying that zone's Y/M/D rather than the instant
 * of midnight there: everything downstream (the datepickers, `toDateString`)
 * reads local getters, so a real instant would shift the day straight back.
 *
 * An unknown or unsupported zone falls back to the browser clock — a report in
 * the wrong zone beats a report that throws.
 */
function startOfDayInZone(date: Date, timeZone?: string): Date {
  if (!timeZone) {
    return startOfDay(date);
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const value = (type: string) => Number(parts.find(part => part.type === type)?.value);
    const year = value('year');
    const month = value('month');
    const day = value('day');
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return startOfDay(date);
    }
    return new Date(year, month - 1, day);
  } catch {
    return startOfDay(date);
  }
}

function addDays(date: Date, days: number): Date {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Sunday of the week containing `date`. */
function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay());
}

function startOfQuarter(date: Date): Date {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

/** Never report past today — future days would dilute per-day averages. */
function clampToToday(range: DateRange, today: Date): DateRange {
  return { start: range.start, end: range.end.getTime() > today.getTime() ? today : range.end };
}

/**
 * @param timeZone the report's business timezone; omit to use the browser's.
 * @returns the range for a preset, or null for 'custom' (the user picks it).
 */
export function resolveDatePreset(
  preset: DatePresetId,
  now: Date = new Date(),
  timeZone?: string
): DateRange | null {
  const today = startOfDayInZone(now, timeZone);

  switch (preset) {
    case 'today':
      return { start: today, end: today };

    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { start: yesterday, end: yesterday };
    }

    case 'this-week':
      return clampToToday(
        { start: startOfWeek(today), end: addDays(startOfWeek(today), 6) },
        today
      );

    case 'last-week': {
      const start = addDays(startOfWeek(today), -7);
      return { start, end: addDays(start, 6) };
    }

    case 'this-month':
      return clampToToday(
        {
          start: new Date(today.getFullYear(), today.getMonth(), 1),
          end: new Date(today.getFullYear(), today.getMonth() + 1, 0),
        },
        today
      );

    case 'last-month':
      return {
        start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        end: new Date(today.getFullYear(), today.getMonth(), 0),
      };

    case 'this-quarter': {
      const start = startOfQuarter(today);
      return clampToToday(
        { start, end: new Date(start.getFullYear(), start.getMonth() + 3, 0) },
        today
      );
    }

    case 'last-quarter': {
      const thisQuarter = startOfQuarter(today);
      const start = new Date(thisQuarter.getFullYear(), thisQuarter.getMonth() - 3, 1);
      return { start, end: new Date(start.getFullYear(), start.getMonth() + 3, 0) };
    }

    case 'this-year':
      return clampToToday(
        {
          start: new Date(today.getFullYear(), 0, 1),
          end: new Date(today.getFullYear(), 11, 31),
        },
        today
      );

    case 'last-year':
      return {
        start: new Date(today.getFullYear() - 1, 0, 1),
        end: new Date(today.getFullYear() - 1, 11, 31),
      };

    case 'custom':
    default:
      return null;
  }
}

/**
 * Which preset, if any, a range corresponds to. Lets the selector snap back to
 * a named preset when the dates happen to match one, instead of always showing
 * "Custom Range" after a programmatic change.
 */
export function matchDatePreset(
  range: DateRange,
  now: Date = new Date(),
  timeZone?: string
): DatePresetId {
  const sameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime();

  for (const option of DATE_PRESET_OPTIONS) {
    if (option.id === 'custom') {
      continue;
    }
    const candidate = resolveDatePreset(option.id, now, timeZone);
    if (candidate && sameDay(candidate.start, range.start) && sameDay(candidate.end, range.end)) {
      return option.id;
    }
  }
  return 'custom';
}
